import { Button, cn, Dialog, Text } from '@risksmart-app/atomic-ui';
import { Trans, useTranslation } from 'react-i18next';

import type { RuleReference } from '../conditionalLogicGraph';

export type DeletionImpact =
  | {
      kind: 'field';
      fieldId: string;
      fieldLabel: string;
      references: RuleReference[];
      fieldOwnRuleCount: number;
    }
  | {
      kind: 'section';
      sectionId: string;
      sectionLabel: string;
      insideFields: { id: string; label: string }[];
      externalReferences: RuleReference[];
      sectionOwnRuleCount: number;
    };

const totalReferenceRuleCount = (references: RuleReference[]) =>
  references.reduce((sum, ref) => sum + ref.ruleCount, 0);

const ReferenceList = ({ references }: { references: RuleReference[] }) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'formEditorDeleteImpactDialog.referenceItem',
  });

  return (
    <ul className={cn('list-disc pl-6')}>
      {references.map((ref) => (
        <li key={`${ref.kind}:${ref.id}`}>
          {t(ref.kind, { label: ref.label, count: ref.ruleCount })}
        </li>
      ))}
    </ul>
  );
};

export const DeleteImpactConfirmationDialog = ({
  impact,
  onCancel,
  onConfirm,
}: {
  impact: DeletionImpact | null;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'formEditorDeleteImpactDialog',
  });
  const open = impact !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onCancel();
        }
      }}
      size={'md'}
    >
      {impact?.kind === 'field' ? (
        <>
          <Dialog.Header title={t('field.title')} />
          <Dialog.Body className={cn('flex flex-col gap-3')}>
            <Text preset={'body-md'}>
              {t('field.intro', { label: impact.fieldLabel })}
            </Text>
            <ul className={cn('list-disc pl-6 flex flex-col gap-1')}>
              {impact.references.length > 0 ? (
                <li>
                  <Trans
                    i18nKey={'formEditorDeleteImpactDialog.field.references'}
                    ns={'common'}
                    values={{
                      count: totalReferenceRuleCount(impact.references),
                    }}
                    components={{ strong: <strong /> }}
                  />
                  <ReferenceList references={impact.references} />
                </li>
              ) : null}
              {impact.fieldOwnRuleCount > 0 ? (
                <li>
                  <Trans
                    i18nKey={'formEditorDeleteImpactDialog.field.ownRules'}
                    ns={'common'}
                    values={{ count: impact.fieldOwnRuleCount }}
                    components={{ strong: <strong /> }}
                  />
                </li>
              ) : null}
            </ul>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant={'destructive'} onClick={onConfirm}>
              {t('field.confirm')}
            </Button>
            <Button variant={'neutral'} style={'outline'} onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Dialog.Footer>
        </>
      ) : null}
      {impact?.kind === 'section' ? (
        <>
          <Dialog.Header
            title={
              impact.externalReferences.length > 0 ||
              impact.sectionOwnRuleCount > 0
                ? t('section.titleWithRules')
                : t('section.title')
            }
          />
          <Dialog.Body className={cn('flex flex-col gap-3')}>
            <Text preset={'body-md'}>
              {t('section.intro', { label: impact.sectionLabel })}
            </Text>
            <ul className={cn('list-disc pl-6 flex flex-col gap-1')}>
              {impact.insideFields.length > 0 ? (
                <li>
                  <Trans
                    i18nKey={
                      'formEditorDeleteImpactDialog.section.insideFields'
                    }
                    ns={'common'}
                    values={{ count: impact.insideFields.length }}
                    components={{ strong: <strong /> }}
                  />
                  <ul className={cn('list-disc pl-6')}>
                    {impact.insideFields.map((field) => (
                      <li key={field.id}>{`"${field.label}"`}</li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li>{t('section.removeOnly')}</li>
              )}
              {impact.externalReferences.length > 0 ? (
                <li>
                  <Trans
                    i18nKey={'formEditorDeleteImpactDialog.section.references'}
                    ns={'common'}
                    values={{
                      count: totalReferenceRuleCount(impact.externalReferences),
                    }}
                    components={{ strong: <strong /> }}
                  />
                  <ReferenceList references={impact.externalReferences} />
                </li>
              ) : null}
              {impact.sectionOwnRuleCount > 0 ? (
                <li>
                  <Trans
                    i18nKey={'formEditorDeleteImpactDialog.section.ownRules'}
                    ns={'common'}
                    values={{ count: impact.sectionOwnRuleCount }}
                    components={{ strong: <strong /> }}
                  />
                </li>
              ) : null}
            </ul>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant={'destructive'} onClick={onConfirm}>
              {t('section.confirm')}
            </Button>
            <Button variant={'neutral'} style={'outline'} onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Dialog.Footer>
        </>
      ) : null}
    </Dialog>
  );
};
